import React from 'react';
import { useTranslation } from 'react-i18next';
import TranslationNamespaces from '../../locales/TranslationNamespaces';
import { useTheme } from '@mui/material';
import { QInputForm, QSpinner } from '@kubiq-base/core';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../state/AppContext';
import { useCache } from '../../state/CachingContext';
import { useApi } from '../../state/ApiContext';
import { IIDProvider } from '../../interfaces/';
import HeaderWithDescription from '../Shared/HeaderWithDescription';
import FlexFormContainer from '../Shared/FlexContainers/FlexFormContainer';
import CenteredQButton from '../Shared/CenteredQButton';
import InvitationMessageBox from '../Shared/MessageBoxes/InvitationMessageBox';
import OrganisationMessageBox from '../Shared/MessageBoxes/OrganisationMessageBox';
import ApiHelper from '../../helpers/ApiHelper';
import SiteHelper from '../../helpers/SiteHelper';

interface Props {
  onComplete: (email: string, idProviders?: IIDProvider[]) => void;
}

const EmailCheckForm = ({ onComplete }: Props): JSX.Element => {
  const namespace = TranslationNamespaces.login;
  const { t } = useTranslation(namespace);
  const theme = useTheme();
  const { postRequest, getRequest } = useApi();
  const { state, setEmail, setTokens, clearTokens } = useAppContext();
  const { getCache, setCache, clearCache } = useCache();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<{ email: string }>();

  const [errorMessage, setErrorMessage] = React.useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isRedirecting, setIsRedirecting] = React.useState(false);

  const navigate = useNavigate();
  const session = getCache();

  const validateEmail = (value: string): string | undefined => {
    setErrorMessage(undefined);
    let error;

    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
      error = 'invalidEmail';
    }

    return error;
  };

  const onSubmit = async (form: { email: string }): Promise<void> => {
    let passedEmailCheck = false;

    setIsSubmitting(true);

    await getRequest<{ data: { emailExists: boolean; verifiedPhone: boolean } }>(
      'User/Emailcheck',
      [{ name: 'email', value: form.email }],
      setIsSubmitting,
    )
      .then((result) => {
        if (result.data) {
          passedEmailCheck = result.data.emailExists;
          if (!passedEmailCheck) {
            setErrorMessage('unknownEmail');
            setEmail('');
          } else {
            setCache('verifiedPhone', result.data.verifiedPhone);
          }
        }
      })
      .catch(() => setErrorMessage('unknownEmail'));

    if (!passedEmailCheck && session && session.invitationguid) {
      setErrorMessage(undefined);
      setCache('email', form.email);
      navigate('/Register');
    } else if (passedEmailCheck && session && session.organisationsapplicationsguid) {
      await SiteHelper.getUserOADetails(form.email, session, setCache, getRequest, setIsSubmitting)
        .then((result) => {
          const providers = result.organisationIdentityProviders;

          if (providers && providers.length > 1) {
            onComplete(form.email, providers);
          } else if (providers && providers.length === 1 && providers[0].identityProviderName === 'AtiQx.ID') {
            if (state.accessToken && state.refreshToken) {
              setIsRedirecting(true);
              SiteHelper.refreshToken(state, session, setTokens, clearCache, postRequest, () => {
                clearTokens();
                setIsRedirecting(false);
              });
            } else {
              onComplete(form.email);
            }
          } else {
            setErrorMessage('emailUnknownForClient');
          }
        })
        .catch(() => {
          setErrorMessage('emailUnknownForClient');
        });
    } else if (passedEmailCheck) {
      onComplete(form.email);
    }
  };

  const isMy = session && session.my;
  const hasOrganisationApplicationGuid = session && session.organisationsapplicationsguid;
  const hasInvitationGuid = session && session.invitationguid;

  return (
    <>
      {isRedirecting ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 72 }}>
          <QSpinner />
        </div>
      ) : (
        <>
          {(hasOrganisationApplicationGuid || isMy) && !hasInvitationGuid && (
            <HeaderWithDescription
              translationNamespace={namespace}
              description='descriptionLoginOrganisation'
              bigHeader
            >
              <OrganisationMessageBox isMy={isMy} />
            </HeaderWithDescription>
          )}
          {hasInvitationGuid && (
            <HeaderWithDescription translationNamespace={namespace} description='descriptionLoginInvitation' bigHeader>
              <InvitationMessageBox />
            </HeaderWithDescription>
          )}
          {!hasOrganisationApplicationGuid && !hasInvitationGuid && !isMy ? (
            <>
              <HeaderWithDescription
                translationNamespace={namespace}
                headerSuffix='NoInfo'
                description='descriptionNoInfo'
                bigHeader
              ></HeaderWithDescription>
              <CenteredQButton variant='outlined' onClick={() => ApiHelper.redirectToMyAtiQxID()}>
                My AtiQx.ID
              </CenteredQButton>
            </>
          ) : (
            <FlexFormContainer onSubmit={handleSubmit(onSubmit)}>
              <QInputForm
                control={control}
                name='email'
                label={t('email')}
                type='email'
                autoFocus
                rules={{ required: 'requiredField', validate: validateEmail }}
                isError={(errors.email && t(errors.email.message as string)) || (errorMessage && t(errorMessage))}
              />

              <CenteredQButton type='submit' isLoading={isSubmitting} fullWidth color='secondary'>
                {t('next')}
              </CenteredQButton>
              {hasInvitationGuid && (
                <CenteredQButton onClick={() => clearCache()} variant='text'>
                  {t('cancel')}
                </CenteredQButton>
              )}
              <div style={{ padding: theme.spacing(2) }}>
                {hasOrganisationApplicationGuid && (
                  <CenteredQButton onClick={() => ApiHelper.redirectToMyAtiQxID()} variant='text'>
                    {t('loginProfile')}
                  </CenteredQButton>
                )}
                <CenteredQButton to='/Register' variant='text'>
                  {t('noAccount')}
                </CenteredQButton>
              </div>
            </FlexFormContainer>
          )}
        </>
      )}
    </>
  );
};

export default EmailCheckForm;
